import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { Avatar } from "@heroui/react";
import { Typography } from "../../../../components";
import { Partner } from "../../../../api/staff";
import { useTranslations } from "../../../../contexts/translation.context";
import { getPartnerLevelConfig } from "../constants";

interface PartnerInfoCellProps {
  partner: Partner;
}

/**
 * PartnerInfoCell Component
 *
 * Displays partner avatar, name and account type badge
 */
const PartnerInfoCell: React.FC<PartnerInfoCellProps> = ({ partner }) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/users/partners/${partner.id}/balance`);
  };

  const levelConfig = useMemo(
    () => getPartnerLevelConfig(partner.lvl, t),
    [partner.lvl, t]
  );

  // Get initials for avatar
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div
      className="flex items-center gap-[12px] py-[8px] cursor-pointer hover:opacity-80 transition-opacity"
      onClick={handleClick}
    >
      <Avatar
        src={partner.avatar}
        alt={partner.name}
        showFallback
        name={getInitials(partner.name)}
        size="sm"
        className="w-[40px] h-[40px] rounded-[12px] bg-gray-200"
        classNames={{
          fallback: "bg-gray-200 text-gray-600"
        }}
      />

      <div className="flex flex-col gap-[4px]">
        <Typography variant="body3" className="!text-[#006FEE] text-[14px] font-semibold hover:underline">
          {partner.name}
        </Typography>

        <span
          className="inline-flex h-[20px] items-center justify-center rounded-lg px-[6px] text-[11px] font-medium capitalize w-fit"
          style={{
            backgroundColor: levelConfig.bg,
            color: levelConfig.text
          }}
        >
          {levelConfig.label}
        </span>
      </div>
    </div>
  );
};

export default PartnerInfoCell;
